'use client';
import { locale } from '@/locale';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import DynamicSelect from './DynamicSelect/Index';
import pulseConfig from '@/pulsex.config';
import Checkbox from './Checkbox';
interface SearchInputProps {
    className?: string;
    setIsLoading?: any;
    setData?: any;
    newspaper?: boolean;
    withFallbackDate?: boolean;
}
const SearchInput = ({
    className,
    setIsLoading,
    setData,
    newspaper = false,
    withFallbackDate = true,
}: SearchInputProps) => {
    const searchParams = useSearchParams();
    const searchDateFrom = searchParams.get('From');
    const searchDateTo = searchParams.get('To');
    const searchQuery = searchParams.get('q');
    const searchAuthor = searchParams.get('Author');
    const searchCategory = searchParams.get('Category');
    const titleParam = searchParams.get('SearchInTitle') === 'true';
    const textParam = searchParams.get('SearchInText') === 'true';

    // Seeded from the URL so a reload or a shared link shows the scope the results use, and
    // kept there by the effect below: the author has to start from the URL too, otherwise
    // submitting after only changing a date drops the ?Author= that is still in the input.
    const [selectedAuthor, setSelectedAuthor] = useState<string | undefined>(
        searchAuthor || undefined
    );
    const [searchInTitle, setSearchInTitle] = useState<boolean>(titleParam);
    const [searchInText, setSearchInText] = useState<boolean>(textParam);

    // Client-side navigation (a submit, or back/forward) changes the params without
    // remounting this component, so without this the controls would keep describing a scope
    // the results no longer use.
    useEffect(() => {
        setSearchInTitle(titleParam);
        setSearchInText(textParam);
        setSelectedAuthor(searchAuthor || undefined);
    }, [titleParam, textParam, searchAuthor]);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const dateFromInputRef = useRef<HTMLInputElement>(null);
    const dateToInputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();

    const currentDate = new Date();
    const startFallbackDate = new Date();
    startFallbackDate.setDate(currentDate.getDate() - 10);

    const fromFallbackDate = startFallbackDate.toISOString().split('T')[0];
    const toFallbackDate = currentDate.toISOString().split('T')[0];

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const searchQuery = searchInputRef.current?.value?.trim() || '';
        const dateFrom = dateFromInputRef.current?.value?.trim();
        const dateTo = dateToInputRef.current?.value?.trim();

        if (!newspaper && (!searchQuery || searchQuery.length <= 2)) {
            console.log('Please enter a search term with more than 2 characters.');
            return;
        }

        const params = new URLSearchParams();

        if (!newspaper && searchQuery) params.set('q', searchQuery);
        if (dateFrom) params.set('From', dateFrom);
        if (dateTo) params.set('To', dateTo);

        if (!newspaper) {
            if (selectedAuthor) params.set('Author', selectedAuthor);
            // No category picker is rendered today; the results page still honours ?Category=
            // when it is present in the URL.
            if (searchCategory) params.set('Category', searchCategory);
            if (searchInTitle) params.set('SearchInTitle', 'true');
            if (searchInText) params.set('SearchInText', 'true');
        }

        const path = newspaper ? '/newspaper' : '/search';
        const queryString = params.toString();

        router.push(queryString ? `${path}?${queryString}` : path);
    };

    const [relatedData, setRelatedData] = useState<any>();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `https://es.al-akhbar.com/api/filters?id=${pulseConfig.CLIENT_ID}`
                );

                if (!response.ok) console.log('Failed to fetch initial filters');

                const result = await response.json();
                if (result?.data) setRelatedData(result?.data);
            } catch (error) {
                console.log('elastic related error', error);
            }
        };

        fetchData();
    }, []);

    const handleAuthorSelect = (option: string) => {
        setSelectedAuthor(option);
    };

    const handleSearchInTitleCheckbox = () => {
        setSearchInTitle(!searchInTitle);
    };

    const handleSearchInTextCheckbox = () => {
        setSearchInText(!searchInText);
    };

    return (
        <>
            <div className="mt-5 w-full">
                <div className="bg-masterRed rounded-tl-3xl w-fit px-4 py-2 text-2xl text-white ltr:text-left">
                    {locale.advancedSearch}
                </div>
                <div className="bg-lightgreyBackground px-4 py-5 rounded-b-3xl">
                    <form onSubmit={handleSubmit} className="space-y-2">
                        {!newspaper ? (
                            <div className="flex flex-col space-y-1 w-full max-w-full">
                                <label htmlFor="" className="block text-xl ltr:text-left">
                                    {locale.searchKeyword}
                                </label>
                                <input
                                    type="text"
                                    id="tags"
                                    ref={searchInputRef}
                                    className="max-lg:text-lg h-[38px] px-2 w-full bg-white rounded-l-full ltr:text-left"
                                    placeholder=""
                                    required
                                    defaultValue={searchQuery || undefined}
                                    // defaultValue={""}
                                    // onChange={handleInputChange}
                                />
                            </div>
                        ) : (
                            ''
                        )}
                        <div className="flex flex-col max-w-full space-y-3 max-md:space-y-0 max-md:flex-row">
                            <div className="flex flex-col space-y-1 w-full max-w-full max-md:w-1/2">
                                <label htmlFor="from" className="text-xl">
                                    {locale.dateFrom}
                                </label>
                                <input
                                    type="date"
                                    name="from"
                                    id="from"
                                    ref={dateFromInputRef}
                                    placeholder={locale.chooseDate}
                                    className="max-lg:text-lg max-md:min-w-[85%] h-[38px] px-2 w-full max-w-full bg-white rounded-l-full"
                                    defaultValue={searchDateFrom || undefined}
                                />
                            </div>
                            <div className="flex flex-col space-y-1 w-full max-w-full max-md:w-1/2 max-md:mr-3">
                                <label htmlFor="to" className="text-xl ltr:text-left">
                                    {locale.dateTo}
                                </label>
                                <input
                                    type="date"
                                    name="to"
                                    id="to"
                                    ref={dateToInputRef}
                                    placeholder={locale.chooseDate}
                                    className="max-lg:text-lg max-md:min-w-[85%] h-[38px] px-2 w-full max-w-full bg-white rounded-l-full"
                                    defaultValue={searchDateTo || undefined}
                                />
                            </div>
                        </div>
                        {!newspaper ? (
                            <div className="flex flex-col max-w-full space-y-3 max-md:space-y-0 max-md:flex-row">
                                <div className="flex flex-col space-y-1 w-full max-w-full max-md:w-1/2 relative">
                                    <label htmlFor="" className="block text-xl">
                                        إسم الكاتب
                                    </label>
                                    <DynamicSelect
                                        key={searchAuthor || 'no-author'}
                                        type={'author'}
                                        inputClassName="rounded-l-full"
                                        initialData={relatedData?.author}
                                        onSelect={handleAuthorSelect}
                                        defaultValue={searchAuthor || undefined}
                                    />
                                </div>

                                <div className="flex flex-col space-y-1 w-full max-w-full max-md:w-1/2 max-md:mr-3 relative">
                                    <Checkbox
                                        label={'إبحث في العنوان'}
                                        checked={searchInTitle}
                                        onChange={handleSearchInTitleCheckbox}
                                    />
                                </div>
                                <div className="flex flex-col space-y-1 w-full max-w-full max-md:w-1/2 max-md:mr-3 relative">
                                    <Checkbox
                                        label={'إبحث في النص'}
                                        checked={searchInText}
                                        onChange={handleSearchInTextCheckbox}
                                    />
                                </div>
                            </div>
                        ) : (
                            ''
                        )}
                        <div className="flex justify-center !mt-5">
                            <button
                                type="submit"
                                className=" w-full max-w-[150px] bg-[#c80f1e] px-8 py-0.5 text-center text-xl text-white rounded-full"
                            >
                                {locale.search}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default SearchInput;
